import { MarkerPreset } from 'services/audio.service';
import { StoryConfig } from 'services/story.service';
import { RootState } from 'store/store';
import { useAppStore } from 'store/store-hooks';
import {
  getCellPayload,
  getCursorCell,
  isDraggingCell,
  markersConversion,
} from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';

type CellPayload = ReturnType<typeof getCellPayload>;
export type SnapToTime = 0 | MarkerId | Duration;
export type SnapToIndex = -1 | Index | null;

export const __useSnapTo = (cell: Index) => {
  const store = useAppStore();

  return (isLeft: boolean, cellData: CellPayload) => {
    const { duration, markers, presets, storyConfig, presetId } = selectData(store.getState());
    if (!duration || !storyConfig || !markers || presetId === null) return;

    let cellPayload = cellData;
    let cursorCell: Index = cell;
    let cursorCellInner: Index | -1 = -1;

    let closestTime: SnapToTime = 0;
    let farthestTime: SnapToTime = 0;
    let closestTimeRange: Seconds = 0;

    const presetMarkers = getMarkersFromPreset(presetId, presets, storyConfig);
    const markersInner = getCellMarkers(presetMarkers, cellPayload, isLeft);

    const onCell = (wsTime: Seconds) => {
      if (isDraggingCell(wsTime, cursorCell, markers, duration)) {
        if (cursorCell === cell) {
          if (isDraggingCell(wsTime, cursorCellInner, markersInner, duration)) return;
          cursorCellInner = getCursorCell(wsTime, markersInner);
          cellPayload = getCellPayload(cursorCellInner, markersInner, duration);
          return;
        }
        cursorCellInner = -1;
        return;
      }
      cursorCell = getCursorCell(wsTime, markers);
      cellPayload = getCellPayload(cursorCell, markers, duration);
    };

    const onSnap = (wsTime: Seconds) => {
      const { leftTime, rightTime } = cellPayload;
      const leftRange = wsTime - leftTime;
      const rightRange = rightTime - wsTime;
      if (leftRange <= rightRange) {
        closestTimeRange = leftRange;
        closestTime = leftTime;
        farthestTime = rightTime;
      } else {
        closestTimeRange = rightRange;
        closestTime = rightTime;
        farthestTime = leftTime;
      }
    };

    const onDrag = (wsTime: Seconds | null) => {
      if (wsTime === null) return getValues();
      onCell(wsTime);
      onSnap(wsTime);
      return getValues();
    };

    const getStraddled = (snapToIndex: SnapToIndex) => {
      if (cursorCellInner !== -1) return [];
      const rangeMarkers = getRangeMarkers(cell, cursorCell);
      if (snapToIndex === null || snapToIndex === -1) return rangeMarkers;
      if (!rangeMarkers.includes(snapToIndex))
        if (snapToIndex < rangeMarkers[0]) rangeMarkers.unshift(snapToIndex);
        else rangeMarkers.push(snapToIndex);
      return rangeMarkers;
    };

    const getInnerMarkers = (wsTime: Seconds): MarkerId[] => {
      if (cursorCellInner === -1) return [];
      const res = sliceInnerMarkers(markersInner, wsTime, isLeft);
      if (!res) return [];
      if (isLeft && res[0] === 0) return res.slice(1);
      if (!isLeft && res.at(-1) === duration) return res.slice(0, -1);
      return res;
    };

    const getValues = () => ({
      closestTime,
      farthestTime,
      closestTimeRange,
    });

    return { onDrag, getStraddled, getInnerMarkers };
  };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { markers, selectedMarkerPreset: presetId, markerPresets: presets, storyConfig } = state.editor;
  const { duration } = state.audio;
  return { duration, markers, presetId, presets, storyConfig };
};

/* H E L P E R S */

const sliceInnerMarkers = (markers: MarkerId[], position: EditorTime, isLeft: boolean) => {
  const index = markers.findIndex((value) => value === position);
  if (index !== -1) return isLeft ? markers.slice(0, index) : markers.slice(index + 1);
  for (let i = 0; i < markers.length - 1; i++) {
    if (markers[i] < position && position < markers[i + 1]) {
      return isLeft ? markers.slice(0, i + 1) : markers.slice(i + 1);
    }
  }
};

const getRangeMarkers = (cellFrom: Index, cellCurr: Index): Index[] => {
  if (cellFrom === cellCurr) return [];
  const length = cellFrom > cellCurr ? cellFrom - cellCurr - 1 : cellCurr - cellFrom - 1;
  const offset = cellFrom > cellCurr ? cellCurr : cellFrom + 1;
  return Array.from({ length }, (_, i) => i + offset);
};

const getMarkersFromPreset = (presetId: MarkerPresetId, presets: MarkerPreset[], storyConfig: StoryConfig) => {
  const { transitionTime, clipTransitionAlignment } = storyConfig;
  const preset = presets.find((el) => el.id === presetId)!.value;
  return markersConversion('fromBE', preset, transitionTime, clipTransitionAlignment);
};

const getCellMarkers = (markers: MarkerId[], cellPayload: CellPayload, isLeft: boolean): MarkerId[] => {
  const { leftTime, rightTime } = cellPayload;
  const nearestTime = isLeft ? leftTime : rightTime;
  const result: Seconds[] = [];
  const step = isLeft ? 1 : -1;

  let i = findDirectionalIndex(markers, nearestTime, isLeft);
  let first = true;

  while (i >= 0 && i < markers.length) {
    const marker = markers[i];
    if (first && marker === nearestTime) {
      first = false;
      i += step;
      continue;
    }
    if (marker <= leftTime || marker >= rightTime) break;
    result.push(marker);
    first = false;
    i += step;
  }
  if (!isLeft) result.reverse();
  return [leftTime, ...result, rightTime];
};

const findDirectionalIndex = (arr: Seconds[], markerTime: Seconds, isLeft: boolean): number => {
  let low = 0;
  let high = arr.length - 1;
  let result = -1;
  /* binary search */
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const midVal = arr[mid];
    if (isLeft) {
      if (midVal >= markerTime) {
        result = mid;
        high = mid - 1;
      } else low = mid + 1;
    } else {
      if (midVal <= markerTime) {
        result = mid;
        low = mid + 1;
      } else high = mid - 1;
    }
  }
  return result;
};
